// src/services/pushDataService.js
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { FirebaseFirestore } from "@capacitor-firebase/firestore";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { getMessaging, getToken, onMessage, isSupported } from "firebase/messaging";

import { app, auth, db } from "../firebase";
import { getCurrentAuthUser } from "./authDataService";
import { isNativeFirestorePlatform } from "./firestorePlatform";
import { FirebaseMessaging } from "@capacitor-firebase/messaging";

// -----------------------------------------------------
// ✅ Konstanten
// -----------------------------------------------------
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

const PUSH_INIT_FLAG = "ee_push_init_done";
const INSTALL_ID_KEY = "ee_push_install_id";
const PUSH_LAST_PROMPT_AT = "ee_push_last_prompt_at";
const DEVICE_ID_KEY = "device_id";

const PUSH_PROMPT_COOLDOWN_MS_MOBILE = 3 * 24 * 60 * 60 * 1000;
const PUSH_PROMPT_COOLDOWN_MS_DESKTOP = 14 * 24 * 60 * 60 * 1000;

let messagingSingleton = null;
let nativeListenersAdded = false;

// -----------------------------------------------------
// ✅ Helpers
// -----------------------------------------------------
const getPlatform = () => {
  try {
    return Capacitor.getPlatform();
  } catch {
    return "web";
  }
};

const isNativePlatform = () => {
  const platform = getPlatform();
  return platform === "ios" || platform === "android";
};

const safeLocalStorageGet = (key) => {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

const safeLocalStorageSet = (key, value) => {
  try {
    if (typeof window === "undefined") return;
    window.localStorage?.setItem(key, value);
  } catch {}
};

const safeLocalStorageGetNumber = (key) => {
  const value = safeLocalStorageGet(key);
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const isMobileUA = () => {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || "");
};

const isSecureContextOk = () =>
  typeof window !== "undefined" &&
  (window.location.protocol === "https:" || window.location.hostname === "localhost");

const getOrCreateInstallId = () => {
  let id = safeLocalStorageGet(INSTALL_ID_KEY);
  if (id) return id;

  id =
    (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
    `inst_${Math.random().toString(16).slice(2)}_${Date.now()}`;

  safeLocalStorageSet(INSTALL_ID_KEY, id);
  return id;
};

const getOrCreateNativeDeviceId = () => {
  let id = safeLocalStorageGet(DEVICE_ID_KEY);
  if (id) return id;

  id = `dev_${Math.random().toString(36).substring(2, 15)}`;
  safeLocalStorageSet(DEVICE_ID_KEY, id);
  return id;
};

const setPendingPushNavigation = (target) => {
  if (!target) return;

  try {
    localStorage.setItem("ee_pending_push_navigation", target);
    window.dispatchEvent(new CustomEvent("ee-push-navigation-ready"));
  } catch (err) {
    console.warn("Push Navigation konnte nicht gespeichert werden:", err);
  }
};
const setForegroundPushNotification = (payload = {}) => {
  try {
    window.dispatchEvent(
      new CustomEvent("ee-foreground-push-received", {
        detail: payload,
      })
    );
  } catch (err) {
    console.warn("Foreground Push Event konnte nicht ausgelöst werden:", err);
  }
};

const handlePushNavigation = (data) => {
  if (!data) return;

  console.log("📦 Push Data:", data);

  const { type, postId, click_action } = data;

  if (type === "directMessage") {
    setPendingPushNavigation("/forum/messages");
    return;
  }

  if (type === "forumReply" && postId) {
    setPendingPushNavigation(`/forum/beitrag/${postId}`);
    return;
  }

  if (type === "system") {
    setPendingPushNavigation("/");
    return;
  }

  if (click_action) {
    setPendingPushNavigation(click_action);
  }
};

// -----------------------------------------------------
// ✅ Web Push Helpers
// -----------------------------------------------------
const getMessagingSafe = async () => {
  if (typeof window === "undefined") return null;

  try {
    const supported = await isSupported();
    if (!supported) return null;

    if (!messagingSingleton) {
      messagingSingleton = getMessaging(app);
    }

    return messagingSingleton;
  } catch {
    return null;
  }
};

const registerFcmServiceWorker = async () => {
  if (typeof window === "undefined") return null;
  if (!("serviceWorker" in navigator)) return null;

  try {
    const reg = await navigator.serviceWorker.register("/firebase-messaging-sw.js", {
      updateViaCache: "none",
    });

    try {
      await navigator.serviceWorker.ready;
    } catch {}

    try {
      if (typeof reg.update === "function") {
        await reg.update();
      }
    } catch {}

    return reg;
  } catch (e) {
    console.warn("[FCM] ServiceWorker registration failed:", e);
    return null;
  }
};

const saveWebTokenForInstall = async ({ uid, token }) => {
  const installId = getOrCreateInstallId();

  await setDoc(
    doc(db, "nutzer", uid, "pushTokens", installId),
    {
      platform: "web",
      token,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  return { installId };
};

// -----------------------------------------------------
// ✅ Native Push Helpers
// -----------------------------------------------------
export const saveNativePushToken = async (token) => {
  try {
    const user = await getCurrentAuthUser();
    if (!user?.uid || !token) return;

    const platform = getPlatform();
    const deviceId = `${platform}_${getOrCreateNativeDeviceId()}`;

    if (isNativeFirestorePlatform()) {
      await FirebaseFirestore.setDocument({
        reference: `nutzer/${user.uid}/devices/${deviceId}`,
        data: {
          token,
          platform,
          updatedAtClient: new Date().toISOString(),
        },
        merge: true,
      });
    } else {
      await setDoc(
        doc(db, "nutzer", user.uid, "devices", deviceId),
        {
          token,
          platform,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    }

    console.log("📲 Native Push Token gespeichert:", deviceId);
  } catch (err) {
    console.error("❌ Fehler beim Speichern des nativen Tokens:", err);
  }
};

export const initNativePush = async () => {
  if (!isNativePlatform()) return null;

  try {
    console.log("📲 initNativePush START", {
      platform: getPlatform(),
    });

    let permStatus = await FirebaseMessaging.checkPermissions();

    console.log("📲 FirebaseMessaging Permission vorher:", permStatus);

    if (
      permStatus.receive === "prompt" ||
      permStatus.receive === "prompt-with-rationale"
    ) {
      permStatus = await FirebaseMessaging.requestPermissions();
      console.log("📲 FirebaseMessaging Permission nach Anfrage:", permStatus);
    }

    if (permStatus.receive !== "granted") {
      console.warn("❌ FirebaseMessaging Permission nicht erteilt:", permStatus);
      return null;
    }

    const tokenResult = await FirebaseMessaging.getToken();

    console.log("✅ FirebaseMessaging Token Result:", tokenResult);

    const token = tokenResult?.token || tokenResult?.value || null;

    if (!token) {
      console.warn("❌ Kein FirebaseMessaging FCM Token erhalten:", tokenResult);
      return null;
    }
    try {
  await FirebaseMessaging.removeAllListeners();

  await FirebaseMessaging.addListener("notificationReceived", (event) => {
  console.log("📩 FirebaseMessaging notificationReceived:", event);

  const data =
    event?.notification?.data ||
    event?.data ||
    {};

  // ✅ App ist offen:
  // Push wird empfangen, aber es findet KEINE Navigation statt.
  // Optional kann hier später ein In-App-Toast/Badge aktualisiert werden.
  try {
    window.dispatchEvent(
      new CustomEvent("ee-foreground-push-received", {
        detail: {
          title: event?.notification?.title || event?.title || "Neue Benachrichtigung",
          body: event?.notification?.body || event?.body || "",
          data,
        },
      })
    );
  } catch (err) {
    console.warn("Foreground Push Event konnte nicht ausgelöst werden:", err);
  }
});

  await FirebaseMessaging.addListener("notificationActionPerformed", (event) => {
    console.log("👉 FirebaseMessaging notificationActionPerformed:", event);

    const data =
      event?.notification?.data ||
      event?.data ||
      event?.actionId?.data ||
      {};

    handlePushNavigation(data);
  });

  console.log("✅ FirebaseMessaging Listener aktiv");
} catch (e) {
  console.warn("FirebaseMessaging Listener konnten nicht gesetzt werden:", e);
}

    await saveNativePushToken(token);

    return token;
  } catch (err) {
    console.error("❌ initNativePush Fehler:", {
      code: err?.code || null,
      message: err?.message || null,
      full: err,
    });

    return null;
  }
};

// -----------------------------------------------------
// ✅ Web Push Public API
// -----------------------------------------------------
export const shouldOfferPushPrompt = () => {
  if (typeof window === "undefined") return false;
  if (typeof Notification === "undefined") return false;
  if (Notification.permission === "granted") return false;

  const mobile = isMobileUA();
  const cooldown = mobile ? PUSH_PROMPT_COOLDOWN_MS_MOBILE : PUSH_PROMPT_COOLDOWN_MS_DESKTOP;
  const last = safeLocalStorageGetNumber(PUSH_LAST_PROMPT_AT);

  if (!last) return true;
  return Date.now() - last > cooldown;
};

export const markPushPromptOfferedNow = () => {
  safeLocalStorageSet(PUSH_LAST_PROMPT_AT, String(Date.now()));
};

export const initWebPushAfterFirstLogin = async ({ force = false } = {}) => {
  console.log("[FCM] initWebPush called, VAPID present:", !!VAPID_KEY);

  try {
    const user = auth.currentUser;
    if (!user?.uid) return { ok: false, reason: "not-logged-in" };

    if (!force) {
      const already = safeLocalStorageGet(PUSH_INIT_FLAG);
      if (already === "1") {
        return { ok: true, skipped: true };
      }
    }

    if (!VAPID_KEY) {
      console.warn("[FCM] Missing REACT_APP_FIREBASE_VAPID_KEY");
      return { ok: false, reason: "missing-vapid" };
    }

    if (!isSecureContextOk()) {
      return { ok: false, reason: "not-secure-context" };
    }

    if (typeof Notification === "undefined") {
      return { ok: false, reason: "no-notification-api" };
    }

    const messaging = await getMessagingSafe();
    if (!messaging) {
      return { ok: false, reason: "unsupported" };
    }

    const swReg = await registerFcmServiceWorker();
    if (!swReg) {
      return { ok: false, reason: "sw-failed" };
    }

    let perm = Notification.permission;
    if (perm === "default") {
      perm = await Notification.requestPermission();
    }

    if (perm !== "granted") {
      return { ok: false, reason: "denied" };
    }

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swReg,
    });

    if (!token) {
      return { ok: false, reason: "no-token" };
    }

    const { installId } = await saveWebTokenForInstall({
      uid: user.uid,
      token,
    });

    safeLocalStorageSet(PUSH_INIT_FLAG, "1");

    return { ok: true, token, installId };
  } catch (e) {
    console.warn("[FCM] initWebPushAfterFirstLogin failed:", e);
    return { ok: false, reason: "error" };
  }
};

export const subscribeToForegroundPush = async (callback) => {
  const messaging = await getMessagingSafe();
  if (!messaging) return () => {};
  if (typeof callback !== "function") return () => {};

  return onMessage(messaging, (payload) => callback(payload));
};

// -----------------------------------------------------
// ✅ Unified Push Init
// -----------------------------------------------------
export const initPushAfterLogin = async ({ force = false } = {}) => {
  if (isNativePlatform()) {
    const token = await initNativePush();
    return token ? { ok: true, token, platform: getPlatform() } : { ok: false, reason: "native-failed" };
  }

  return await initWebPushAfterFirstLogin({ force });
};

export const isNativePushPlatform = () => isNativePlatform();